import { defineStore } from 'pinia';
import type { LocationQuery } from 'vue-router';
import type { FamilyPlanDetails } from '~/models/cellphone_page/FamilyPlanDetails.model';
import type {
  AggregatedPlanDto,
  ColorDto,
  LabelDto,
  ListingProduct,
  VariantDto,
  AggregatedBundlePriceDto,
  AggregatedProductPriceDto,
  AggregatedPaymentDto,
  AggregatedPopupDiscountDto,
  AggregatedDiscountDto,
} from '~/models/ListingProduct.api.model';
import { NEW_CONDITION } from '~/models/ListingProduct.api.model';
import {
  ALLOWED_MONTHLY_PAYMENT_PERIODS,
  DEFAULT_MONTHLY_PAYMENT_PERIOD,
  QUERY_BUNDLE_PRICE_ID,
  QUERY_MONTHLY_PAYMENT_PERIOD,
  QUERY_COLOR,
  QUERY_COND,
  QUERY_STORAGE,
  DEFAULT_PLAN_ID,
} from '~/utils/pdp/queryParamHelpers';

type InitialStateFromProductSelectedInListing = {
  isStudentSelected?: boolean;
  condition?: string;
  color?: string;
  storageSize?: string;
  downPaymentTime?: string;
  planId?: string;
};

export interface ExistingStorageOptionsByColor {
  [key: string]: string[];
}

export type ProductState = {
  addedPopupDiscounts: number[];
  familyPlanDetails?: FamilyPlanDetails;
  isStudentSelected: boolean;
  selectedColor: string;
  selectedCondition: string;
  selectedDownPaymentTime: string;
  selectedPlanId: string;
  selectedStorage: string;
};

export type UI = {
  loading: {
    isFamilySummaryTableLoading: boolean;
  };
};

export type ProductPageState = {
  product?: ListingProduct;
  productState: ProductState;
  ui: UI;
};

export type ProductPageTrackingData = {
  productPriceId: number;
  downPayTime: string;
  ecommerce: {
    items: unknown[];
  };
};

export const useMyProductPageStore = defineStore('ProductPageStore', {
  state: (): ProductPageState => ({
    product: undefined,
    productState: {
      addedPopupDiscounts: [],
      familyPlanDetails: undefined,
      isStudentSelected: false,
      selectedColor: '',
      selectedCondition: '',
      selectedDownPaymentTime: DEFAULT_MONTHLY_PAYMENT_PERIOD,
      selectedPlanId: DEFAULT_PLAN_ID,
      selectedStorage: '',
    },
    ui: {
      loading: {
        isFamilySummaryTableLoading: false,
      },
    },
  }),
  actions: {
    queryToMonthlyPaymentPeriod(query?: LocationQuery): string | undefined {
      if (!query || !query[QUERY_MONTHLY_PAYMENT_PERIOD]) {
        return undefined;
      }

      const monthlyPaymentPeriod = Array.isArray(
        query[QUERY_MONTHLY_PAYMENT_PERIOD],
      )
        ? (query[QUERY_MONTHLY_PAYMENT_PERIOD][0] ??
          DEFAULT_MONTHLY_PAYMENT_PERIOD)
        : query[QUERY_MONTHLY_PAYMENT_PERIOD];

      if (ALLOWED_MONTHLY_PAYMENT_PERIODS.includes(monthlyPaymentPeriod)) {
        return monthlyPaymentPeriod;
      }

      return undefined;
    },
    queryToPlanId(query?: LocationQuery): string | undefined {
      if (!query || !query[QUERY_BUNDLE_PRICE_ID]) {
        return undefined;
      }

      // get plan id from query
      const bundlePriceFromQuery = Array.isArray(query[QUERY_BUNDLE_PRICE_ID])
        ? +(query[QUERY_BUNDLE_PRICE_ID][0] || -1)
        : +query[QUERY_BUNDLE_PRICE_ID];

      const bpIdToPlanIdMap = pdp_createBundlePriceIdToPlanIdMap(
        this.product?.Variants,
      );

      const planId = bpIdToPlanIdMap[bundlePriceFromQuery];

      if (planId) {
        const planIdString = `${planId ?? ''}`;
        return planIdString;
      }

      return undefined;
    },
    changeIsStudent(isStudentSelected: boolean): void {
      this.productState.isStudentSelected = isStudentSelected;
      const currentVariant = this.currentVariant;
      const currentPlanName = this.currentPlan?.PlanName;
      const currentBindPeriod = this.selectedBindingPeriod;

      const newId =
        currentVariant?.Prices.BundlePrices?.filter(
          (bundle) =>
            bundle.Plan?.IsStudent === isStudentSelected &&
            bundle.BindingPeriod.Months === currentBindPeriod &&
            bundle.Plan?.PlanName === currentPlanName &&
            bundle.Plan?.IsFamilyPlan === this.isFamilyPlanSet,
        )?.at(0)?.Plan?.PlanId ??
        currentVariant?.Prices.BundlePrices?.filter(
          (bundle) =>
            bundle.Plan?.IsStudent === isStudentSelected &&
            bundle.BindingPeriod.Months === currentBindPeriod &&
            bundle.Plan?.PlanName === '30GB',
        )?.at(0)?.Plan?.PlanId;

      this.productState.selectedPlanId = newId + '';
    },
    populatePACState(product: ListingProduct, query?: LocationQuery): void {
      const planStore = usePlanStore();
      this.product = product;
      this.productState.familyPlanDetails = undefined;

      const paramsToPacIdMap = pdp_createParamsToPacIdMap(product?.Variants);
      const paramMapKey = pdp_createParamMapKeyFromParams(query);
      const initialStateFromParams = paramsToPacIdMap[paramMapKey] ?? null;

      const firstVariant = product.Variants[0];

      this.productState.addedPopupDiscounts = [];

      this.productState.selectedColor =
        initialStateFromParams?.colorName ??
        this.initialStateFromProductSelectedInListing?.color ??
        firstVariant.Color.ColorName;

      this.productState.selectedCondition =
        initialStateFromParams?.condition ??
        this.initialStateFromProductSelectedInListing?.condition ??
        firstVariant.Condition;

      this.productState.selectedDownPaymentTime =
        this.queryToMonthlyPaymentPeriod(query) ??
        this.initialStateFromProductSelectedInListing?.downPaymentTime ??
        DEFAULT_MONTHLY_PAYMENT_PERIOD;

      // Ensure a valid planId is selected, fallback to the first available plan if DEFAULT_PLAN_ID is not found
      const planId = this.availablePlans?.some(
        (plan) => plan.PlanId.toString() === DEFAULT_PLAN_ID,
      )
        ? DEFAULT_PLAN_ID
        : (this.availablePlans?.[0]?.PlanId.toString() ?? DEFAULT_PLAN_ID);

      this.productState.selectedPlanId =
        this.queryToPlanId(query) ??
        this.initialStateFromProductSelectedInListing?.planId ??
        planId;

      this.productState.selectedStorage =
        initialStateFromParams?.storageSize || firstVariant.StorageSize;

      this.productState.selectedStorage =
        initialStateFromParams?.storageSize ??
        this.initialStateFromProductSelectedInListing?.storageSize ??
        firstVariant.StorageSize;

      this.productState.isStudentSelected = planStore.studentPlanIds.includes(
        +this.productState.selectedPlanId,
      );
    },
    setFamilyPlan(
      familyPlanDetails: FamilyPlanDetails,
      parentPlanId: number,
    ): void {
      this.productState.familyPlanDetails = familyPlanDetails;

      // If existing customer, set the family connection's plan
      // id to show correct prices for the product
      if (
        familyPlanDetails.isNewCustomer === false &&
        !!familyPlanDetails.familyPlanPlanId
      ) {
        this.productState.selectedPlanId = `${familyPlanDetails.familyPlanPlanId}`;
      } else {
        this.productState.selectedPlanId = `${parentPlanId}`;
      }
    },
    resetSelectedStorageIfNotAvailableForChosenColor(): void {
      const storageOptionStringsDisplaying = this.storageOptionsWithThisColor;
      const canCurrentSelectedStorageBeDisplayed =
        storageOptionStringsDisplaying.includes(
          this.productState.selectedStorage,
        );

      if (
        !canCurrentSelectedStorageBeDisplayed &&
        storageOptionStringsDisplaying.length > 0
      ) {
        this.productState.selectedStorage = storageOptionStringsDisplaying[0];
      }
    },
    resetSelectedConditionIfNotAvailableForChosenColor(): void {
      const conditionOptionStringsDisplaying = this.conditionOptionsToDisplay;
      const canCurrentSelectedConditionBeDisplayed =
        conditionOptionStringsDisplaying.includes(
          this.productState.selectedCondition,
        );

      if (
        !canCurrentSelectedConditionBeDisplayed &&
        conditionOptionStringsDisplaying.length > 0
      ) {
        this.productState.selectedCondition =
          conditionOptionStringsDisplaying[0];
      }
    },
    changeOption(
      optionName: 'storage' | 'colors' | 'condition' | 'payment' | 'plans',
    ) {
      // Change url to current product link on state change without reloading page
      let newUrl = this.product?.ProductLinkUrl;

      const variant = this.currentVariant;
      if (!variant) {
        return;
      }

      const newQueryList: string[] = [];
      const currentQuery = useRequestURL().searchParams;

      if (
        (currentQuery.get(QUERY_COLOR) || optionName === 'colors') &&
        variant.Color.ColorName !== this.defaultVariant?.Color.ColorName
      ) {
        const colorValue = pdp_toQueryParamValue(variant.Color.ColorName);
        newQueryList.push(`${QUERY_COLOR}=${colorValue}`);
      }

      if (
        (currentQuery.get(QUERY_STORAGE) || optionName === 'storage') &&
        variant.StorageSize !== this.defaultVariant?.StorageSize
      ) {
        const storageValue = pdp_toQueryParamValue(variant.StorageSize);
        newQueryList.push(`${QUERY_STORAGE}=${storageValue}`);
      }

      if (
        (currentQuery.get(QUERY_COND) || optionName === 'condition') &&
        variant.Condition !== NEW_CONDITION
      ) {
        const conditionValue = pdp_toQueryParamValue(variant.Condition);
        newQueryList.push(`${QUERY_COND}=${conditionValue}`);
      }

      if (
        currentQuery.get(QUERY_MONTHLY_PAYMENT_PERIOD) ||
        optionName === 'payment'
      ) {
        const monthlyPaymentPeriodValue =
          this.productState.selectedDownPaymentTime;
        newQueryList.push(
          `${QUERY_MONTHLY_PAYMENT_PERIOD}=${monthlyPaymentPeriodValue}`,
        );
      }

      if (currentQuery.get(QUERY_BUNDLE_PRICE_ID) || optionName === 'plans') {
        const newBundlePriceId = (
          this.currentBundlePrice as AggregatedBundlePriceDto
        )?.BundlePriceId;
        const value = newBundlePriceId
          ? `${newBundlePriceId}`
          : `${currentQuery.get(QUERY_BUNDLE_PRICE_ID)}`;
        newQueryList.push(`${QUERY_BUNDLE_PRICE_ID}=${value}`);
      }

      const newQueryString = newQueryList
        .map((param, index) => {
          const symbol = index === 0 ? '?' : '&';
          return `${symbol}${param}`;
        })
        .join('');

      newUrl += newQueryString;

      if (newUrl) {
        const router = useRouter();
        router.replace(newUrl);
      }
      if (optionName === 'colors') {
        this.resetSelectedStorageIfNotAvailableForChosenColor();
        this.resetSelectedConditionIfNotAvailableForChosenColor();
      }
    },
    resetStateAfterFamilyDeselection(): void {
      if (this.productState.familyPlanDetails?.isNewCustomer === false) {
        this.productState.selectedPlanId = DEFAULT_PLAN_ID;
      }

      this.productState.familyPlanDetails = undefined;
      return;
    },
  },
  getters: {
    colorOptions(state: ProductPageState): ColorDto[] {
      const colors = state.product?.Variants?.map(
        (variant: VariantDto) => variant.Color,
      );

      type ColorDtoByColorString = { [key: string]: ColorDto };

      const uniqueColors = colors?.reduce<ColorDtoByColorString>(
        (group: ColorDtoByColorString, color) => {
          group[color.Color] = color;
          return group;
        },
        {},
      );

      if (!uniqueColors) {
        return [];
      }

      return Object.keys(uniqueColors).map((key) => uniqueColors[key]);
    },
    storageOptionsWithThisColor(state: ProductPageState): string[] {
      const storageOptionsWithThisColor = state.product?.Variants?.filter(
        (variant: VariantDto) =>
          variant.Color.ColorName == state.productState.selectedColor,
      );

      const storages =
        storageOptionsWithThisColor?.map((variant) => variant.StorageSize) ??
        [];

      const parseFunction = (storageString: string) =>
        +storageString.replace('GB', '000').replace('TB', '000000');

      if (storages) {
        storages.sort((a, b) => parseFunction(a) - parseFunction(b));
      }

      return [...new Set(storages)];
    },
    conditionOptionsToDisplay(state: ProductPageState): string[] {
      const variants = state.product?.Variants.filter(
        (variant) =>
          variant.Color.ColorName === state.productState.selectedColor &&
          variant.StorageSize === state.productState.selectedStorage,
      );

      return variants?.map((variant) => variant.Condition) || [];
    },
    onlyNewConditionOptionExists(): boolean {
      const options = this.conditionOptionsToDisplay;
      return options && options.length === 1 && options[0] === NEW_CONDITION;
    },
    currentVariant(state: ProductPageState): VariantDto | undefined {
      return (
        state.product?.Variants.find(
          (variant) =>
            variant.Color.ColorName === state.productState.selectedColor &&
            variant.Condition === state.productState.selectedCondition &&
            variant.StorageSize === state.productState.selectedStorage,
        ) || state.product?.Variants[0]
      );
    },
    defaultVariant(state: ProductPageState): VariantDto | undefined {
      if (
        !state.product?.Variants ||
        !(
          Array.isArray(state.product?.Variants) &&
          state.product.Variants.length > 0
        )
      ) {
        return undefined;
      }

      return state.product?.Variants[0];
    },
    currentPlan(): AggregatedPlanDto | undefined {
      return this.currentBundlePrice?.Plan;
    },
    currentFamilyParentPlansForDrawer(): AggregatedBundlePriceDto[] {
      return (this.currentVariant?.Prices.BundlePrices || []).filter(
        (bp) =>
          bp.Plan?.IsFamilyPlan === false &&
          bp.Plan?.IsFamilyAllowed === true &&
          bp.Plan?.IsStudent === false &&
          ([30, 100].includes(bp.Plan.DataAllowance) ||
            [30, 100].includes(bp.Plan.RegularDataAllowance)),
      );
    },
    labels(): LabelDto[] {
      const currentBundlePrice = this.currentBundlePrice;
      if (!currentBundlePrice) {
        return [];
      }

      const labels: LabelDto[] = [];

      if (this.currentBundlePrice?.Urgency) {
        labels.push({
          Text: this.currentBundlePrice?.Urgency,
          Color: 'few-left',
        } as LabelDto);
      }

      if (this.currentVariant?.Prices?.Label) {
        labels.push(this.currentVariant?.Prices?.Label);
      }

      for (const label of currentBundlePrice.Labels || []) {
        labels.push(label);
      }

      const uniqueLabels = Array.from(
        new Set(labels.map((label) => JSON.stringify(label))),
      ).map((jsonString) => JSON.parse(jsonString)) as LabelDto[];

      return uniqueLabels;
    },
    currentBundlePrice(
      state: ProductPageState,
    ): AggregatedBundlePriceDto | AggregatedProductPriceDto | undefined {
      const selectedVariant = this.currentVariant;
      const selectedBindingPeriod = this.selectedBindingPeriod;

      // If family plan as existing customer is set, the prices need to come from
      // a family connection bundleprice object as you don't get same savings on
      // the product since you don't buy the phone and a main plan together
      if (
        this.isFamilyPlanSet &&
        state.productState.familyPlanDetails?.isNewCustomer === false &&
        state.productState.familyPlanDetails.familyPlanPlanId
      ) {
        return selectedVariant?.Prices?.BundlePrices?.find(
          (bundlePrice) =>
            bundlePrice.Plan?.PlanId ===
              state.productState.familyPlanDetails?.familyPlanPlanId &&
            bundlePrice.BindingPeriod.Months === selectedBindingPeriod,
        );
      }

      if (selectedVariant?.Prices.Price) {
        return selectedVariant?.Prices.Price;
      } else {
        return selectedVariant?.Prices?.BundlePrices?.find(
          (bundle) =>
            bundle.Plan?.PlanId === +this.productState.selectedPlanId &&
            bundle.BindingPeriod.Months === selectedBindingPeriod,
        );
      }
    },
    currentAccessoryPrice(): AggregatedPaymentDto | undefined {
      return this.currentVariant?.Prices.Price?.Payments.at(0);
    },
    currentAccessoryLowestPartialPayment(): AggregatedPaymentDto | undefined {
      const hasMultiplePayments =
        (this.currentVariant?.Prices.Price?.Payments.length || 0) > 1;

      return (
        (hasMultiplePayments &&
          this.currentVariant?.Prices.Price?.Payments.at(-1)) ||
        undefined
      );
    },
    relevantBundlePrices(): AggregatedBundlePriceDto[] | undefined {
      return this.currentVariant?.Prices.BundlePrices?.filter(
        (bundle) =>
          (bundle.BindingPeriod.Months === 0 &&
            +this.productState.selectedDownPaymentTime === 1) ||
          (bundle.BindingPeriod.Months !== 0 &&
            +this.productState.selectedDownPaymentTime !== 1),
      );
    },
    availablePlans(): AggregatedPlanDto[] | undefined {
      const bundlesByDownPayment = this.relevantBundlePrices;

      const plans = bundlesByDownPayment?.map((bundle) => bundle.Plan);

      const filtered =
        plans?.filter(
          (plan): plan is AggregatedPlanDto =>
            !!plan &&
            plan?.IsStudent === this.productState.isStudentSelected &&
            !plan.IsFamilyPlan,
        ) || [];

      const uniquePlansGroups = filtered?.reduce(
        (group: { [key: string]: AggregatedPlanDto }, plan) => {
          group[plan?.PlanName || 'error'] = plan;
          return group;
        },
        {},
      );

      const uniquePlans = Object.keys(uniquePlansGroups).map(
        (key) => uniquePlansGroups[key],
      );

      if (uniquePlans) {
        uniquePlans.sort((a, b) => b.DataAllowance - a.DataAllowance);
      }

      return uniquePlans;
    },
    popupDiscounts(): AggregatedPopupDiscountDto[] {
      const discounts = this.currentBundlePrice?.Discounts ?? [];

      return discounts
        .filter(
          (discount): discount is AggregatedDiscountDto =>
            discount.IsPopup && discount.PopupContent !== undefined,
        )
        .flatMap((discount) =>
          discount.PopupContent!.map((content) => ({
            Component: 'Popup',
            DiscountId: discount.DiscountId,
            DisplayOrder: discount.DisplayOrder,
            Title: content.Title,
            SubTitle: content.SubTitle,
            ImageUrl: content.ImageUrl ?? '',
            PopupPriceMonthly: discount.PopupPriceMonthly,
            PopupPrice: discount.PopupPrice,
            ShortDescription: content.ShortDescription,
            ChargingInfo: content.ChargingInfo,
            UspItems: content.UspItems ?? [],
          })),
        );
    },
    currentPaymentData(
      state: ProductPageState,
    ): AggregatedPaymentDto | undefined {
      return this.currentBundlePrice?.Payments.find(
        (payment) =>
          payment.Time === +state.productState.selectedDownPaymentTime,
      );
    },
    pricesOptions(state: ProductPageState): AggregatedPaymentDto[] {
      const currentVariant = this.currentVariant;
      if (
        !currentVariant ||
        !currentVariant.Prices ||
        !currentVariant.Prices.BundlePrices
      ) {
        return [];
      }

      const paymentOptions = pdp_selectMonthlyPaymentOptions(
        currentVariant.Prices.BundlePrices,
        +state.productState.selectedPlanId,
      );

      return paymentOptions;
    },
    isFamilyPlanSet(state: ProductPageState): boolean {
      return (
        !!state.productState.familyPlanDetails?.numberOfConnections &&
        !!state.productState?.familyPlanDetails.familyPlansTotalMonthlyFee
      );
    },
    currentTotalMonthlyPrice(state: ProductPageState): number {
      if (this.isFamilyPlanSet) {
        const familyFee =
          state.productState.familyPlanDetails?.familyPlansTotalMonthlyFee || 0;
        return this.selectedBindingPeriod === 0
          ? familyFee
          : familyFee + (this.currentPaymentData?.DownPayment || 0);
      }

      return this.selectedBindingPeriod === 0
        ? this.currentBundlePrice?.Plan?.MonthlyFee || 0
        : this.currentPaymentData?.MonthlyPrice || 0;
    },
    currentTotalMonthlyPriceToDisplay(): string {
      return pdp_formatPrice(this.currentTotalMonthlyPrice);
    },
    currentTotalRegularMonthlyPrice(state: ProductPageState): number {
      const currentRegularMonthlyPrice =
        this.currentPaymentData?.RegularMonthlyPrice ||
        this.currentPaymentData?.MonthlyPrice ||
        0;

      if (this.isFamilyPlanSet) {
        const plansTotalRegularMonthlyFee =
          state.productState.familyPlanDetails
            ?.familyPlansTotalRegularMonthlyFee || 0;
        const plansTotalMonthlyFee =
          state.productState.familyPlanDetails?.familyPlansTotalMonthlyFee || 0;

        const plansTotalFee =
          plansTotalRegularMonthlyFee || plansTotalMonthlyFee;
        const hardwarePrice = this.currentPaymentData?.DownPayment || 0;

        return plansTotalFee + hardwarePrice;
      }

      return currentRegularMonthlyPrice;
    },
    currentTotalRegularMonthlyPriceToDisplay(): string {
      return pdp_formatPrice(this.currentTotalRegularMonthlyPrice, '');
    },
    currentMonthlyPaymentTextToDisplay(): string {
      if (
        this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount &&
        this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount !== 0 &&
        this.currentPaymentData?.Time &&
        this.currentPaymentData?.Time <
          this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount
      ) {
        return 'i ' + this.currentPaymentData?.Time + ' mån*';
      }

      if (
        this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount &&
        this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount !== 0
      ) {
        return (
          'i ' +
          this.currentBundlePrice?.Plan?.OfferValidityInMonthsDiscount +
          ' mån*'
        );
      }
      return 'i ' + this.currentPaymentData?.Time + ' mån';
    },
    currentTotalDirectPriceToDisplay(): string {
      if (this.selectedBindingPeriod !== 0) {
        return '0';
      }

      return pdp_formatPrice(this.currentPaymentData?.DownPayment, '0');
    },
    currentFullPriceStringForScreenReader(): string {
      const nonDiscountedPriceString =
        this.currentTotalRegularMonthlyPriceToDisplay &&
        this.currentTotalRegularMonthlyPriceToDisplay !==
          this.currentTotalMonthlyPriceToDisplay
          ? `Tidigare ${this.currentTotalRegularMonthlyPriceToDisplay} kronor i månaden, nu `
          : '';

      const inNumberOfMonthsString =
        this.currentPaymentData?.Time !== 1
          ? ` i ${this.currentPaymentData?.Time} månader`
          : '';

      return `${nonDiscountedPriceString}${this.currentTotalMonthlyPriceToDisplay} kronor i månaden${inNumberOfMonthsString}. ${this.currentTotalDirectPriceToDisplay} kronor att betala nu.`;
    },
    currentTotalSavings(): number | null {
      if (this.currentPaymentData?.Time === 1) {
        return null;
      }

      let savings: number | undefined = undefined;

      if (
        this.isFamilyPlanSet &&
        this.productState.familyPlanDetails?.familyPlanSummaryItems &&
        this.productState.familyPlanDetails?.familyPlanSummaryItems.length
      ) {
        savings = 0;
        const planItems =
          this.productState.familyPlanDetails?.familyPlanSummaryItems.slice();
        for (const planItem of planItems) {
          savings += planItem.Savings;
        }
      } else {
        savings = this.currentBundlePrice?.Savings;
      }

      return savings && savings > 0 ? savings : null;
    },
    currentTotalSavingsToDisplay(): string | null {
      return pdp_formatPrice(this.currentTotalSavings || undefined, '');
    },
    initialStateFromProductSelectedInListing():
      | InitialStateFromProductSelectedInListing
      | undefined {
      const filterStore = useFilterStore();
      const productSelectedOnListingPage = filterStore.selectedProduct;

      if (!productSelectedOnListingPage) {
        return undefined;
      }

      const selectedProductPlanId =
        productSelectedOnListingPage?.bundlePrice?.Plan?.PlanId;
      const selectedProductDownPaymentTime =
        productSelectedOnListingPage?.payment?.Time;
      const selectedProductCondition =
        productSelectedOnListingPage?.variant?.Condition;
      const selectedProductColor =
        productSelectedOnListingPage?.variant?.Color.ColorName;
      const selectedProductStorageSize =
        productSelectedOnListingPage?.variant?.StorageSize;

      const planId = selectedProductPlanId
        ? `${selectedProductPlanId}`
        : undefined;
      const downPaymentTime = selectedProductDownPaymentTime
        ? `${selectedProductDownPaymentTime}`
        : undefined;
      const condition = selectedProductCondition
        ? `${selectedProductCondition}`
        : undefined;
      const color = selectedProductColor
        ? `${selectedProductColor}`
        : undefined;
      const storageSize = selectedProductStorageSize
        ? `${selectedProductStorageSize}`
        : undefined;
      const isStudentSelected =
        productSelectedOnListingPage?.bundlePrice?.Plan?.IsStudent ?? undefined;

      return {
        planId,
        downPaymentTime,
        condition,
        color,
        storageSize,
        isStudentSelected,
      };
    },
    totalCostOverBindingPeriod(state: ProductPageState): number | undefined {
      if (this.isFamilyPlanSet && this.selectedBindingPeriod !== 0) {
        return state.productState.familyPlanDetails
          ?.totalCostOverBindingPeriodIncludingFamilyPlans;
      }

      return (this.currentBundlePrice as AggregatedBundlePriceDto | undefined)
        ?.TotalCost;
    },
    totalCostOverBindingPeriodToDisplay(): string {
      const stringIfNoPrice = 'Pris kan inte visas';
      return pdp_formatPrice(this.totalCostOverBindingPeriod, stringIfNoPrice);
    },
    trackingData(): ProductPageTrackingData {
      const planStore = usePlanStore();
      const plan = this.currentPlan;
      const familyPlans =
        this.productState?.familyPlanDetails?.familyPlanSummaryItems;
      const bundlePrice = this.currentBundlePrice;

      const baseItem = {
        down_pay_time: this.productState?.selectedDownPaymentTime,
        item_brand: this.product?.ManufacturerName,
        item_category: this.product?.Category,
        item_id: this.currentVariant?.ProductAttributeCombinationId,
        item_name: this.product?.ProductName,
        item_variant: `${this.productState?.selectedStorage ? this.productState?.selectedStorage + ' ' : ''}${this.productState?.selectedColor ?? ''}`,
        price:
          (bundlePrice as AggregatedBundlePriceDto | undefined)?.ProductPrice ??
          this.currentAccessoryPrice?.DownPayment,
        quantity: 1,
      };

      const trackingData: ProductPageTrackingData = {
        productPriceId:
          (bundlePrice as AggregatedBundlePriceDto | undefined)
            ?.BundlePriceId ?? 0,
        downPayTime: this.productState?.selectedDownPaymentTime,
        ecommerce: {
          items: [baseItem],
        },
      };

      const addPlanItem = (plan: AggregatedPlanDto, isFamilyPlan: boolean) => {
        trackingData.ecommerce.items.push({
          item_brand: 'Halebop',
          item_category: 'Abonnemang',
          item_id: planStore.planIdToPacIdMap[plan.PlanId] || 0,
          item_name: isFamilyPlan
            ? `Halebop Familj ${plan.PlanName}`
            : `Halebop ${plan.PlanName}`,
          item_variant:
            this.productState.selectedDownPaymentTime === '1'
              ? '0 månader'
              : '24 månader',
          price: plan.MonthlyFee,
          quantity: 1,
        });
      };

      if (familyPlans) {
        familyPlans.forEach((familyPlan) => {
          if (familyPlan.Plan) {
            addPlanItem(familyPlan.Plan, familyPlan.Plan.IsFamilyPlan);
          }
        });
      } else if (plan) {
        addPlanItem(plan, plan.IsFamilyPlan);
      }

      const addDiscountProductToTracking = (discountProduct: any) => {
        const isItemAlreadyTracked = trackingData.ecommerce.items.some(
          (item: any) =>
            item.item_id === discountProduct.ProductAttributeCombinationId,
        );

        if (!isItemAlreadyTracked) {
          trackingData.ecommerce.items.push({
            item_brand: discountProduct.Manufacturer,
            item_category: discountProduct.Category,
            item_id: discountProduct.ProductAttributeCombinationId,
            item_name: discountProduct.ProductName,
            item_variant:
              discountProduct.InsuranceLevel ?? discountProduct.Color,
            price: discountProduct.Price,
            quantity: 1,
          });
        }
      };

      this.currentBundlePrice?.Discounts?.forEach(
        (discount: AggregatedDiscountDto) => {
          if (
            this.productState.addedPopupDiscounts.includes(discount.DiscountId)
          ) {
            discount?.DiscountProducts?.forEach(addDiscountProductToTracking);
          }

          if (
            discount.DiscountType === 'FreeProduct' &&
            discount.DiscountProducts
          ) {
            discount.DiscountProducts.forEach(addDiscountProductToTracking);
          }
        },
      );

      return trackingData;
    },
    productSeoTitleAsProductVariant(state: ProductPageState): string {
      let title = `${state.product?.ManufacturerName} ${state.product?.ProductName}`;

      if (this.currentVariant?.StorageSize) {
        title += ` ${this.currentVariant?.StorageSize}`;
      }

      title += ` ${this.currentVariant?.Color.ColorName}`;

      return title;
    },
    productSeoTitleWithoutVariant(state: ProductPageState): string {
      return `${state.product?.ManufacturerName} ${state.product?.ProductName}`;
    },
    productSeoImageUrl(): string | undefined {
      return this.currentVariant?.Images.at(0)?.PictureUrl;
    },
    selectedBindingPeriod(): number {
      return determineSelectedBindingPeriod(
        +this.productState.selectedDownPaymentTime,
      );
    },
  },
});
